import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT, useLanguage } from "@/lib/LanguageContext";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import frLocale from "@fullcalendar/core/locales/fr";
import esLocale from "@fullcalendar/core/locales/es";
import { AlertTriangle } from "lucide-react";
import {
  eventColor, errMsg, localeFor, openHoursColor, BUFFER_COLOR,
  CLAIMING_BOOKING_STATUSES, MOVABLE_BOOKING_STATUSES,
} from "./booking/calendarUtils";
import { rescheduleErrKey } from "./booking/rescheduleErrors";
import { CalendarToolbar } from "./booking/CalendarToolbar";
import { GoogleCalendarCard } from "./booking/GoogleCalendarCard";
import { ConnectGateBanner } from "./booking/ConnectGateBanner";
import { EventDetailDialog } from "./booking/EventDetailDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const FC_LOCALE = { fr: "fr", en: "en", es: "es" };

// The two refusals of a move the practitioner may walk past: the target is outside their
// own open hours, or on one of their own Google events. Everything else — another client
// on one of those minutes (TIME_UNAVAILABLE), a move to where it already is, the booking
// having moved under this request — is a message and a revert, never a choice.
const OVERRIDABLE_MOVE_REFUSALS = ["OUTSIDE_AVAILABILITY", "TARGET_GCAL_BUSY"];

const MINUTE_MS = 60000;

/**
 * The practitioner's calendar.
 *
 * THREE LAYERS, and only one of them is interactive:
 *   1. the OPEN HOURS — GET /admin/open-hours for the visible range: weekly windows and
 *      extra hours, minus closures, as real instants. A read-only BACKGROUND layer: start
 *      times are computed per service when a client looks, so there is no opening to
 *      click, edit or drag. A draft schedule is tinted differently and named above the
 *      grid, because its hours are not bookable yet;
 *   2. the APPOINTMENTS — GET /admin/bookings, each drawn from its own `slot_start` for
 *      its own `slot_duration_min`, with its stamped `buffer_after_min` as a faint
 *      trailing background block (the time it occupies beyond the appointment itself).
 *      Dragging one reschedules it: POST /admin/bookings/{id}/reschedule {new_start};
 *   3. the practitioner's own GOOGLE events (GET /tenant/calendar/busy), read-only.
 */
export default function BookingCalendar() {
  const t = useT("admin");
  const { lang } = useLanguage();
  const [bookings, setBookings] = useState([]);
  const [openHours, setOpenHours] = useState({ status: null, windows: [] });
  const [busy, setBusy] = useState([]);
  const [detail, setDetail] = useState(null);
  const [connection, setConnection] = useState(null);
  // The visible range FullCalendar reports (`datesSet`), so the open-hours layer can be
  // re-read for the same range after a change without waiting for a navigation.
  const [range, setRange] = useState(null);
  // An overridable refusal the practitioner is being asked about: the dropped booking,
  // its target, FullCalendar's `revert` for the optimistic move, and the refusal code so
  // the dialog can name the actual obstacle.
  const [pendingOverride, setPendingOverride] = useState(null);
  // AlertDialogAction AND AlertDialogCancel are both DialogPrimitive.Close internally
  // (Radix): both fire onOpenChange(false) after their own onClick. This flag tells
  // "already handled by confirm/cancel" apart from "implicit close (Escape)" — only the
  // latter must still revert.
  const overrideResolvedRef = useRef(false);
  // BUG-058 — the FullCalendar instance, so the mismatch pill can move the view to the
  // week of the appointment it opens. Nothing else drives the calendar imperatively.
  const calendarRef = useRef(null);

  const loadBookings = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/bookings");
      setBookings(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(errMsg(e, t, "cal.loadError"));
    }
  }, [t]);

  const loadOpenHours = useCallback(async (r) => {
    if (!r) return;
    try {
      const { data } = await api.get("/admin/open-hours", {
        params: { from: r.start.toISOString(), to: r.end.toISOString() },
      });
      setOpenHours({ status: data?.status ?? null, windows: Array.isArray(data?.windows) ? data.windows : [] });
    } catch (e) {
      // The layer is context, not the calendar's subject: without it the appointments
      // still stand. Said once, and the layer is emptied rather than left stale.
      setOpenHours({ status: null, windows: [] });
      toast.error(errMsg(e, t, "cal.openHoursLoadError"));
    }
  }, [t]);

  const loadBusy = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant/calendar/busy");
      // Only the practitioner's external Google events (not Solunary bookings pushed out).
      setBusy((data.events || []).filter((e) => !e.is_solunary && e.start && !e.all_day));
    } catch (e) {
      setBusy([]);
    }
  }, []);

  const loadConnection = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant/calendar/connection");
      setConnection(data);
    } catch (e) {
      setConnection({ configured: false, connected: false });
    }
  }, []);

  useEffect(() => { loadBookings(); loadBusy(); loadConnection(); }, [loadBookings, loadBusy, loadConnection]);
  useEffect(() => { loadOpenHours(range); }, [range, loadOpenHours]);

  // FullCalendar calls this on mount and on every navigation or view change. A new object
  // per call, but the same instants mean the same range: only a real change re-reads.
  const onDatesSet = useCallback((info) => {
    setRange((prev) => (prev && prev.start.getTime() === info.start.getTime()
      && prev.end.getTime() === info.end.getTime()
      ? prev : { start: info.start, end: info.end }));
  }, []);

  // Combined refetch handed to GoogleCalendarCard as `onRefresh` and to the detail dialog
  // as `onChanged`: connection plus the aggregated mismatch_count, AND the bookings (a
  // sync or a reconcile can change sync_status/needs_client_notice).
  const refreshAll = useCallback(async () => {
    await Promise.all([loadBookings(), loadConnection()]);
  }, [loadBookings, loadConnection]);

  const connected = !!connection?.connected;

  // Which bookings are drawn. A cancelled appointment holds no time, so it is left off
  // the grid — except one Google cancelled that still awaits the practitioner's
  // acknowledgement (`needs_client_notice`): that one stays visible, flagged, until it is
  // acknowledged, because the mismatch pill counts it.
  const shownBookings = useMemo(
    () => bookings.filter((b) => b.slot_start && (b.status !== "cancelled" || b.needs_client_notice)),
    [bookings],
  );

  // BUG-058 — THE PILL'S CLICK ASKS THE SERVER, because the pill's NUMBER does: the count
  // comes from `GET /tenant/calendar/connection` over EVERY booking of the practice, and
  // `GET /tenant/calendar/mismatches` returns exactly that set, oldest first. Two things
  // happen on a hit: the calendar MOVES to the appointment's week (a dialog over the wrong
  // week says something is wrong and not where), and the appointment opens. The loaded
  // booking row is preferred (it is the whole document); the server's projection stands in
  // when this screen has not loaded it.
  const showMismatches = useCallback(async () => {
    let items = [];
    try {
      const { data } = await api.get("/tenant/calendar/mismatches");
      items = data?.items || [];
    } catch (e) {
      toast.error(errMsg(e, t, "cal.loadError"));
      return;
    }
    if (!items.length) {
      // The number was stale (something else acknowledged it, or a reconcile healed it):
      // say so and refetch, rather than leaving a pill nothing can clear.
      toast.info(t("sync.noneLeft"));
      loadConnection();
      return;
    }
    const first = items[0];
    const b = bookings.find((x) => x.id === first.id) || first;
    const start = new Date(b.slot_start);
    const cal = calendarRef.current?.getApi();
    if (cal && !Number.isNaN(start.getTime())) cal.gotoDate(start);
    setDetail(b);
  }, [bookings, t, loadConnection]);

  // The move itself. `allowOutside` is the retry after the practitioner confirmed the
  // override dialog, and is sent ONLY then — a flag pinned on every drag would silently
  // reinstate the two things it exists to make visible (moving outside your own hours,
  // and moving onto one of your own Google events).
  const moveBooking = useCallback(async (booking, newStart, revertFn, { allowOutside = false } = {}) => {
    try {
      const { data } = await api.post(`/admin/bookings/${booking.id}/reschedule`, {
        new_start: new Date(newStart).toISOString(),
        ...(allowOutside ? { allow_outside_availability: true } : {}),
      });
      toast.success(t("cal.apptRescheduled", {
        time: new Date(data?.slot_start || newStart).toLocaleString(localeFor(lang), {
          weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
        }),
      }));
      await loadBookings();
    } catch (e) {
      const code = e?.response?.data?.detail;
      if (!allowOutside && OVERRIDABLE_MOVE_REFUSALS.includes(code)) {
        // Keep the event where it was dropped and ask. The revert travels with the
        // pending decision — cancelling (or Escape) is what puts the event back.
        overrideResolvedRef.current = false;
        setPendingOverride({ booking, newStart, revert: revertFn, reason: code });
        return;
      }
      const key = rescheduleErrKey(code);
      toast.error(key ? t(key) : errMsg(e, t));
      revertFn?.(); // the DOM already moved optimistically
    }
  }, [loadBookings, t, lang]);

  const confirmOverride = useCallback(() => {
    if (!pendingOverride) return;
    overrideResolvedRef.current = true;
    moveBooking(pendingOverride.booking, pendingOverride.newStart, pendingOverride.revert,
      { allowOutside: true });
    setPendingOverride(null);
  }, [pendingOverride, moveBooking]);

  const cancelOverride = useCallback(() => {
    if (!pendingOverride) return;
    overrideResolvedRef.current = true;
    pendingOverride.revert?.();
    setPendingOverride(null);
  }, [pendingOverride]);

  // FullCalendar event objects: resolved colours (native props, see
  // calendarUtils.eventColor) and `raw` — the business object — in extendedProps for the
  // click and drop handlers below.
  const fcEvents = useMemo(() => {
    const openLayer = openHours.windows.map((w, i) => ({
      id: `open-${i}-${w.start}`,
      start: w.start,
      end: w.end,
      display: "background",
      backgroundColor: openHoursColor(openHours.status),
      classNames: [openHours.status === "published" ? "sol-fc-open" : "sol-fc-open-draft"],
      extendedProps: { raw: { kind: "open", ...w } },
    }));

    const bookingEvents = [];
    for (const b of shownBookings) {
      const start = new Date(b.slot_start);
      if (Number.isNaN(start.getTime())) continue;
      const end = new Date(start.getTime() + (Number(b.slot_duration_min) || 60) * MINUTE_MS);
      const mismatchKind = b.sync_status === "drift" ? "drift" : (b.needs_client_notice ? "notice" : null);
      const colors = eventColor({ kind: "booking", booking: b });
      const movable = MOVABLE_BOOKING_STATUSES.includes(b.status);
      const classNames = ["sol-fc-booked"];
      if (mismatchKind) classNames.push("sol-fc-mismatch");
      bookingEvents.push({
        id: `booking-${b.id}`,
        title: `${b.firstName} ${b.lastName}`,
        start,
        end,
        editable: movable,
        startEditable: movable,
        durationEditable: false,
        backgroundColor: colors.bg,
        borderColor: colors.border,
        textColor: colors.text,
        classNames,
        extendedProps: { raw: { kind: "booking", booking: b, mismatchKind } },
      });
      // The buffer it occupies after the appointment: background, so it can be neither
      // clicked nor dragged on its own — it follows its appointment on the next load.
      const buffer = Number(b.buffer_after_min) || 0;
      if (buffer > 0 && CLAIMING_BOOKING_STATUSES.includes(b.status)) {
        bookingEvents.push({
          id: `buffer-${b.id}`,
          start: end,
          end: new Date(end.getTime() + buffer * MINUTE_MS),
          display: "background",
          backgroundColor: BUFFER_COLOR,
          classNames: ["sol-fc-buffer"],
          extendedProps: { raw: { kind: "buffer", bookingId: b.id } },
        });
      }
    }

    const busyEvents = busy.map((e) => {
      const colors = eventColor({ kind: "external" });
      const start = new Date(e.start);
      return {
        id: `gcal-${e.id}`,
        title: e.summary,
        start,
        end: e.end ? new Date(e.end) : new Date(start.getTime() + 60 * MINUTE_MS),
        editable: false,
        backgroundColor: colors.bg,
        borderColor: colors.border,
        textColor: colors.text,
        extendedProps: { raw: { kind: "external", id: e.id, summary: e.summary } },
      };
    });

    return [...openLayer, ...bookingEvents, ...busyEvents];
  }, [openHours, shownBookings, busy]);

  const onEventClick = useCallback((info) => {
    const raw = info.event.extendedProps.raw;
    if (raw?.kind !== "booking") return; // open hours, buffers and Google events are read-only
    setDetail(raw.booking);
  }, []);

  const onEventDrop = useCallback((info) => {
    const raw = info.event.extendedProps.raw;
    if (raw?.kind !== "booking") { info.revert(); return; }
    moveBooking(raw.booking, info.event.start, info.revert);
  }, [moveBooking]);

  const renderEventContent = useCallback((arg) => {
    const raw = arg.event.extendedProps.raw;
    if (raw?.kind === "external") {
      return (
        <div className="flex items-center gap-1 px-1 text-[11px] w-full overflow-hidden" data-testid={`cal-busy-${raw.id}`} title={`Google: ${raw.summary}`}>
          {arg.timeText && <span className="font-medium shrink-0">{arg.timeText}</span>}
          <span className="truncate">{raw.summary}</span>
        </div>
      );
    }
    if (raw?.kind !== "booking") return null; // background layers carry no content
    const b = raw.booking;
    const name = `${b.firstName} ${b.lastName}`;
    return (
      <div
        className="flex items-center gap-1 px-1 text-[11px] w-full overflow-hidden"
        data-testid={`cal-event-${b.id}`}
        title={`${b.service} — ${name}`}
      >
        {raw.mismatchKind && <AlertTriangle size={10} className="shrink-0" data-testid={`cal-mismatch-${b.id}`} />}
        {arg.timeText && <span className="font-medium shrink-0">{arg.timeText}</span>}
        <span className="truncate">{name}</span>
      </div>
    );
  }, []);

  const fcLocale = FC_LOCALE[lang] || "en";

  return (
    <div data-testid="booking-calendar" className="space-y-4">
      <CalendarToolbar />

      <GoogleCalendarCard status={connection} onRefresh={refreshAll} onSynced={loadBusy} onShowMismatches={showMismatches} />

      {connection && !connected && (
        <ConnectGateBanner showButton={false} configured={connection?.configured !== false} />
      )}

      {openHours.status !== "published" && openHours.windows.length > 0 && (
        <div className="text-sm bg-eh-ochre/10 border border-eh-ochre-deep/30 text-eh-ochre-deep rounded-xl px-4 py-2" data-testid="cal-open-hours-draft">
          {t("cal.openHoursDraft")}
        </div>
      )}

      <div className="bg-white border border-eh-border-soft rounded-2xl overflow-hidden p-2 sol-fc-wrap">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          headerToolbar={{ left: "prev,next today", center: "title", right: "dayGridMonth,timeGridWeek,timeGridDay" }}
          initialView="timeGridWeek"
          firstDay={1}
          slotMinTime="07:00:00"
          slotMaxTime="21:00:00"
          height="auto"
          nowIndicator
          locale={fcLocale}
          locales={[frLocale, esLocale]}
          editable
          eventDurationEditable={false}
          // No appointment is dropped into the past: the server refuses it
          // (TIME_UNAVAILABLE, not overridable), so the grid does not offer the gesture.
          eventAllow={(span) => span.start > new Date()}
          events={fcEvents}
          datesSet={onDatesSet}
          eventClick={onEventClick}
          eventDrop={onEventDrop}
          eventContent={renderEventContent}
        />
      </div>

      {/* refreshAll (not just the bookings): the dialog's re-push / acknowledge actions
          also change mismatch_count on the connection, which the strip must reflect. */}
      {detail && <EventDetailDialog booking={detail} onClose={() => setDetail(null)} onChanged={refreshAll} />}

      {/* The target is outside the practitioner's open hours, or on one of their Google
          events. The server refused; the practitioner may insist (confirm-then-retry,
          including the implicit cancellation on Escape). */}
      <AlertDialog
        open={!!pendingOverride}
        onOpenChange={(open) => {
          if (open) return;
          if (!overrideResolvedRef.current) pendingOverride?.revert?.();
          overrideResolvedRef.current = false;
          setPendingOverride(null);
        }}
      >
        <AlertDialogContent data-testid="cal-override-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("cal.confirmOverrideTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(pendingOverride?.reason === "TARGET_GCAL_BUSY"
                ? "cal.confirmOverrideBusyBody"
                : "cal.confirmOverrideOutsideBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelOverride} data-testid="cal-override-cancel">{t("cal.confirmOverrideCancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmOverride} data-testid="cal-override-confirm-btn">{t("cal.confirmOverrideConfirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
